import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Record, RecordStatus } from '../../types/records';
import { format } from 'date-fns';
import { Edit, Trash2 } from 'lucide-react';

const adminPath = process.env.REACT_APP_ADMIN_PATH || '/sh-yonetim-kapisi-x7';

interface ContentCardProps {
  record: Record;
  onDelete: (id: string) => void;
  variants: any;
}

const statusColorMap: { [key in RecordStatus]: string } = {
  AÇIK: 'bg-green-500/20 text-green-300 border-green-500/30',
  KAPALI: 'bg-red-500/20 text-red-300 border-red-500/30',
  DİKKAT: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  İPTAL: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
};

const categoryColorMap: { [key: string]: string } = {
    'Etkinlikler': 'bg-sky-500/20 text-sky-300 border-sky-500/30',
    'Konserler': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    'Doğa Güzellikleri': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    'Beklenmedik Olaylar': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    'Devletçe Kapalı': 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    'Popüler Olanlar': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    'Rehber': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
};


const ContentCard: React.FC<ContentCardProps> = ({ record, onDelete, variants }) => {
  const navigate = useNavigate();

  const handleDelete = () => {
    if (window.confirm(`'${record.title}' başlıklı kaydı silmek istediğinizden emin misiniz?`)) {
      onDelete(record.id);
    }
  };

  const handleEdit = () => {
    navigate(`${adminPath}/edit-content/${record.id}`);
  };

  return (
    <motion.div
      variants={variants}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-blue-800/50 backdrop-blur-sm border border-blue-700/50 rounded-xl shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow">
        {/* Header with Status and Category */}
        <div className="flex justify-between items-center mb-4">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${categoryColorMap[record.category] || 'bg-gray-500/20 text-gray-300'}`}>
            {record.category}
          </span>
          <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${statusColorMap[record.status] || 'bg-gray-500/20 text-gray-300'}`}>
            {record.status}
          </span>
        </div>

        {/* Title and Location */}
        <h3 className="text-xl font-bold text-white mb-1">{record.title}</h3>
        <p className="text-sm text-blue-300">{record.cityName}{record.districtName ? `, ${record.districtName}`: ''}</p>

        {/* Last Updated */}
        <p className="text-xs text-blue-400 mt-4">
          Son Güncelleme: {format(record.lastUpdated, 'dd.MM.yyyy HH:mm')}
        </p>
      </div>
      
      {/* Footer with Actions */}
      <div className="bg-blue-900/50 border-t border-blue-700/50 p-4 flex justify-end space-x-3 rounded-b-xl">
        <button
          onClick={handleEdit}
          className="px-4 py-2 text-sm font-semibold text-blue-200 bg-blue-800/80 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit size={16} className="inline mr-2" />
          Düzenle
        </button>
        <button
          onClick={handleDelete}
          className="px-4 py-2 text-sm font-semibold text-red-300 bg-red-500/20 rounded-lg hover:bg-red-500/40 transition-colors"
        >
           <Trash2 size={16} className="inline mr-2" />
           Sil
        </button>
      </div>
    </motion.div>
  );
};

export default ContentCard;
